import { supabase } from './supabase';

/**
 * Facturation côté admin client — factures Odoo du resto via l'Edge Function
 * `odoo-invoices` (JWT vérifié + contrôle d'appartenance côté serveur).
 * Résilient : renvoie ok/erreur, ne jette jamais.
 */

export interface OdooInvoice {
  id: number;
  name: string;
  date: string | null;
  dueDate: string | null;
  /** Montant total (devise du resto). */
  total: number;
  /** Reste dû. */
  residual: number;
  state: 'draft' | 'posted' | 'cancel' | string;
  paymentState: 'not_paid' | 'in_payment' | 'paid' | 'partial' | 'reversed' | string;
  currency: string | null;
}

export interface LoadInvoicesResult {
  ok: boolean;
  invoices: OdooInvoice[];
  error?: string;
}

/** Message neutre affiché au resto quand la facturation Odoo est momentanément KO. */
export const CLIENT_SAFE_INVOICE_MESSAGE = 'Vos factures seront disponibles très prochainement.';

// Motifs techniques à ne JAMAIS montrer au resto (auth, clé API, secrets, XML-RPC).
const TECHNICAL_ERROR_PATTERNS: RegExp[] = [
  /authentification/i,
  /USER\s*\/\s*API_KEY/i,
  /api[_\s-]?key/i,
  /auth[_\s-]?refused/i,
  /secrets?\b/i,
  /ODOO_/,
  /xml-?rpc/i,
];

/**
 * Neutralise tout message technique avant affichage au CLIENT (resto).
 * Garde-fou pérenne : même si l'Edge Function renvoie un jour un message brut
 * (clé expirée, secret manquant…), le resto ne voit qu'un message rassurant.
 */
export function clientSafeError(raw: string | null | undefined): string {
  const msg = (raw ?? '').trim();
  if (!msg) return CLIENT_SAFE_INVOICE_MESSAGE;
  if (TECHNICAL_ERROR_PATTERNS.some((re) => re.test(msg))) return CLIENT_SAFE_INVOICE_MESSAGE;
  return msg;
}

/**
 * Extrait le VRAI message d'erreur renvoyé par l'Edge Function (corps JSON),
 * au lieu du générique « Edge Function returned a non-2xx status code ».
 */
export async function extractFnError(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.clone().json();
      if (body && typeof body.error === 'string' && body.error) return body.error;
    } catch { /* ignore : on garde le message générique */ }
  }
  return (error as { message?: string })?.message || fallback;
}

/** Liste les factures Odoo du resto. */
export async function loadOdooInvoices(tenantSlug: string): Promise<LoadInvoicesResult> {
  const { data, error } = await supabase.functions.invoke('odoo-invoices', {
    body: { tenant_slug: tenantSlug, action: 'list' },
  });
  if (error) return { ok: false, invoices: [], error: clientSafeError(await extractFnError(error, '')) };
  const res = (data ?? {}) as { ok?: boolean; error?: string; invoices?: OdooInvoice[] };
  if (!res.ok) return { ok: false, invoices: [], error: clientSafeError(res.error) };
  return { ok: true, invoices: Array.isArray(res.invoices) ? res.invoices : [] };
}

export interface InvoicePdfResult {
  ok: boolean;
  filename?: string;
  pdfBase64?: string;
  error?: string;
}

/** Récupère le PDF d'une facture (base64). */
export async function loadInvoicePdf(tenantSlug: string, invoiceId: number): Promise<InvoicePdfResult> {
  const { data, error } = await supabase.functions.invoke('odoo-invoices', {
    body: { tenant_slug: tenantSlug, action: 'pdf', invoice_id: invoiceId },
  });
  if (error) return { ok: false, error: clientSafeError(await extractFnError(error, '')) };
  const res = (data ?? {}) as { ok?: boolean; error?: string; filename?: string; pdf_base64?: string };
  if (!res.ok) return { ok: false, error: clientSafeError(res.error) };
  return { ok: true, filename: res.filename, pdfBase64: res.pdf_base64 };
}

/** Déclenche le téléchargement d'un PDF base64 dans le navigateur. */
export function downloadBase64Pdf(base64: string, filename: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename || 'facture.pdf';
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Libellé FR de l'état de paiement Odoo. */
export function paymentLabel(state: string): string {
  switch (state) {
    case 'paid':
      return 'Payée';
    case 'in_payment':
      return 'En cours de paiement';
    case 'partial':
      return 'Partiellement payée';
    case 'reversed':
      return 'Annulée';
    case 'not_paid':
      return 'Non payée';
    default:
      return state || '—';
  }
}
